import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Select,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Box,
  OutlinedInput,
} from '@mui/material';
import {
  InfoOutlined,
  Add as AddIcon,
} from '@mui/icons-material';
import { useFeathers } from '../../app/util';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { AddDistrictTypeModal } from '../AddDistrictTypeModal';
import { CreatePartyModal } from '../CreatePartyModal';
import PromptEditor from '../PromptEditor';

const PRIMARY_MODE_OPTIONS = [
  {
    value: 'PRIMARY_MODE_BY_PARTY',
    label: 'Open Primary',
    description: 'This is an open primary election where the candidates are split by party. Voters can vote in any election.',
  },
  {
    value: 'PRIMARY_MODE_BY_PARTY_CLOSED_SEMI',
    label: 'Semi-Closed Primary',
    description: 'This is a primary election where the candidates are split by party, and only voters registered with that party can vote in that primary. Voters who are registered independent can vote for any party.',
  },
  {
    value: 'PRIMARY_MODE_BY_PARTY_CLOSED',
    label: 'Closed Primary',
    description: 'This is a primary election where the candidates are split by party and only voters registered with that party can vote in that primary. Voters who are registered independent cannot vote in any party\'s primary.',
  },
  {
    value: 'PRIMARY_MODE_TOP_TWO',
    label: 'Top-Two Primary',
    description: 'This is a primary election where the top two candidates move on to the general election. All candidates participate in the same primary election.',
  },
  {
    value: 'PRIMARY_MODE_TOP_FOUR',
    label: 'Top-Four Primary',
    description: 'This is a primary election where the top four candidates move on to the general election. All candidates participate in the same primary election.',
  },
];

const PRIORITY_PARTY_KEYS = ['D', 'R', 'N'];

const sortPartyOptions = (parties) => {
  if (!Array.isArray(parties)) return [];
  return [...parties].sort((a, b) => {
    const aIdx = PRIORITY_PARTY_KEYS.indexOf(a.key);
    const bIdx = PRIORITY_PARTY_KEYS.indexOf(b.key);
    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
    if (aIdx >= 0) return -1;
    if (bIdx >= 0) return 1;
    return (a.key || '').localeCompare(b.key || '');
  });
};

const RUNOFF_MODE_OPTIONS = [
  {
    value: 'RUNOFF_MODE_NO_RUNOFF',
    label: 'No Runoff',
    description: 'This is a runoff election in which the top candidate wins.',
  },
  {
    value: 'RUNOFF_MODE_50_PERCENT_REQUIRED',
    label: '50% Required',
    description: 'This is a runoff election in which the top vote getter must receive at least 50% of the votes to win. If not, then the top two vote-getters move to a runoff election.',
  },
  {
    value: 'RUNOFF_MODE_30_PERCENT_REQUIRED',
    label: '30% Required',
    description: 'This is a runoff election in which the top vote getter must receive at least 30% of the votes to win. If not, then the top two vote-getters move to a runoff election.',
  },
];

const UNCONTESTED_PRIMARIES_OPTIONS = [
  {
    value: 'yes',
    label: 'All appear on ballot',
    description: 'Show all uncontested primary races on the ballot.',
    displayTitle: 'Uncontested primaries appear on ballot',
    displayDescription: 'People will see uncontested primary races on their ballot.',
  },
  {
    value: 'no',
    label: 'None appear on ballot',
    description: 'Hide all uncontested primary races from the ballot.',
    displayTitle: 'Uncontested primaries don’t appear on ballot',
    displayDescription: 'People will not see uncontested primary races on their ballot.',
  },
  {
    value: 'statewide-only',
    label: 'Statewide and congressional only',
    description: 'Show uncontested statewide and congressional races, but hide other uncontested primary races.',
    displayTitle: 'Only statewide and congressional uncontested primaries appear on ballot',
    displayDescription: 'People will see uncontested statewide and congressional primary races, but not other uncontested primary races, on their ballot.',
  },
];

const StateElectionConfiguration = ({ editing, onEditingChange }) => {
  const matchName = useParams().matchName;
  const feathers = useFeathers();
  const [stateElectionConfig, setStateElectionConfig] = useState(null);
  const [stateConfiguration, setStateConfiguration] = useState(null);
  const [partyOptions, setPartyOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Generate human-readable scouting configuration description
  const generateScoutingDescription = (eauType, scoutingConfigForType, typeConfigurations) => {
    const fullCoverage = scoutingConfigForType.fullCoverage || [];
    const partialCoverage = scoutingConfigForType.partialCoverage || [];
    
    const eauTypeName = humanizeDistrictType(eauType, typeConfigurations);
    
    // Filter to only tier 2 types
    const fullCoverageTier2 = fullCoverage.filter(type => {
      const typeConfig = typeConfigurations.find(tc => tc.type === type);
      return typeConfig && typeConfig.tierNumber === 2;
    });
    
    const partialCoverageTier2 = partialCoverage.filter(type => {
      const typeConfig = typeConfigurations.find(tc => tc.type === type);
      return typeConfig && typeConfig.tierNumber === 2;
    });
    
    const fullCoverageNames = fullCoverageTier2.map(type => humanizeDistrictType(type, typeConfigurations));
    const partialCoverageNames = partialCoverageTier2.map(type => humanizeDistrictType(type, typeConfigurations));
    
    let description = `Whenever we upload a candidate list for ${eauTypeName}, `;
    
    if (fullCoverageNames.length > 0 && partialCoverageNames.length > 0) {
      description += `we will also mark all ${fullCoverageNames.join(', ')} types as complete. We will only mark ${partialCoverageNames.join(', ')} types as complete if they are explicitly in the content.`;
    } else if (fullCoverageNames.length > 0) {
      description += `we will also mark all ${fullCoverageNames.join(', ')} types as complete.`;
    } else if (partialCoverageNames.length > 0) {
      description += `we will only mark ${partialCoverageNames.join(', ')} types as complete if they are explicitly in the content.`;
    } else {
      description += `no additional district types will be marked as complete.`;
    }
    
    return description;
  };

  const loadStateElectionConfig = async () => {
    const stateCode = matchName.split('-')[0];
    try {
      const [config, stateConfig] = await Promise.all([
        feathers.getService('state-election-configurations').get(stateCode.toUpperCase()).catch(() => null),
        feathers.getService('state-configurations').get(stateCode).catch(() => null),
      ]);
      
      if (config) {
        // set the default auto scouter configuration on the client side
        const defaultSelectPrompt = [
          `We're trying to find sample ballot info and qualified candidate list information for "{districtName}", {stateName}.`,
          `This should be specifically for the upcoming {electionName} election.`,
          `Review the search results and choose the single best result that is most likely to contain this information from an official government source (such as the state, county, or local elections office).`,
          `Prioritize official government domains over third-party sites, news articles, or general voter information tools.`,
          `Avoid links that are clearly about election results, general voter lookup tools, meeting minutes, agendas, or election calendars unless they obviously contain sample ballots or candidate lists.`,
          `Return only the link of the best search result.`
        ].join('\n');
        const defaultQuery = '{stateName} - {districtName} - upcoming elections';
        const defaultCrawlPrompt = [
          'You are starting on the page {startingUrl}, which is the homepage or main elections page for {districtName}, {stateName}. We are trying to find a list of ballot content for the upcoming election: {electionName}.',
          'Your goal is to find any sample ballots and qualified candidate lists that apply to the entire area (not just a single precinct unless that is the only option).',
          'If you find anything related to the {electionName} election, you may need to click on that heading to expand sections or load additional content.',
          'Sometimes, for an entire county, there may be multiple sample ballots (for each municipality or precinct). In that case you should surface a list of those links rather than picking only one.',
          'This ballot content is usually located on a page or under a heading called "Sample ballots", "Sample ballot", "Qualified candidates", or something very similar. Prefer these pages over general election calendars or result pages.',
          'We strongly recommend that you completely scroll the current page first to look for sample ballot or qualified candidate content before clicking into subpages or navigation links that do not clearly mention ballots or candidates.',
          'If you find a list of sample ballots, scroll to the bottom of the list to ensure you are not missing any entries before concluding you are done.',
          'Avoid clicking into pages that are clearly about election results, generic voter lookup tools (like "My Voter Page"), board meeting pages, or general election calendars unless they obviously contain sample ballot or candidate-list content.',
          'As you find links that appear to be sample ballots or candidate lists, you MUST call the function "report_possible_links" with the link labels. You can call this multiple times as you continue exploring; it helps track progress.',
          'When you are confident you have found all relevant links worth exploring, call the function "finish_crawling" exactly once with your final list of links and any useful notes.',
          'If you get stuck, or you opened a tab or PDF that is a dead end, use the function "go_back" to navigate back or close the current tab so you can continue exploring other paths.',
        ].join('\n');
        if (!config.autoScouter) {
          config.autoScouter = {
            search: {
              mode: 'google',
              query: defaultQuery,
              selectPrompt: defaultSelectPrompt,
            },
            crawl: {
              crawlPrompt: defaultCrawlPrompt,
            },
            parse: {
              sampleBallot: null,
              candidateList: null,
              measureList: null,
            },
          };
        } else {
          // Ensure search object exists and has sensible defaults
          if (!config.autoScouter.search) {
            config.autoScouter.search = {
              mode: 'google',
              query: defaultQuery,
              selectPrompt: defaultSelectPrompt,
            };
          } else {
            if (!config.autoScouter.search.mode) {
              config.autoScouter.search.mode = 'google';
            }
            if (!config.autoScouter.search.query) {
              config.autoScouter.search.query = defaultQuery;
            }
            if (config.autoScouter.search.selectPrompt === undefined || config.autoScouter.search.selectPrompt === null) {
              config.autoScouter.search.selectPrompt = defaultSelectPrompt;
            }
          }

          // Ensure crawl object exists and has a sensible default
          if (!config.autoScouter.crawl) {
            config.autoScouter.crawl = {
              crawlPrompt: defaultCrawlPrompt,
            };
          } else if (!config.autoScouter.crawl.crawlPrompt) {
            config.autoScouter.crawl.crawlPrompt = defaultCrawlPrompt;
          }

          // Ensure parse override fields exist without replacing system defaults
          if (!config.autoScouter.parse) {
            config.autoScouter.parse = {
              sampleBallot: null,
              candidateList: null,
              measureList: null,
            };
          } else {
            if (config.autoScouter.parse.sampleBallot === undefined) {
              config.autoScouter.parse.sampleBallot = null;
            }
            if (config.autoScouter.parse.candidateList === undefined) {
              config.autoScouter.parse.candidateList = null;
            }
            if (config.autoScouter.parse.measureList === undefined) {
              config.autoScouter.parse.measureList = null;
            }
          }
        }
        // Seed nonpartisanPrimaryMode so it is never null (backend requires a value)
        if (config.nonpartisanPrimaryMode === undefined || config.nonpartisanPrimaryMode === null) {
          config.nonpartisanPrimaryMode = 'NONPARTISAN_PRIMARY_MODE_DECIDED_IN_GENERAL';
        }
        if (config.uncontestedPrimariesAppearOnBallot === false) {
          config.uncontestedPrimariesAppearOnBallot = 'no';
        } else if (
          config.uncontestedPrimariesAppearOnBallot === true ||
          config.uncontestedPrimariesAppearOnBallot === undefined ||
          config.uncontestedPrimariesAppearOnBallot === null
        ) {
          config.uncontestedPrimariesAppearOnBallot = 'yes';
        }
        setStateElectionConfig(config);
      } else {
      }
      
      if (stateConfig) {
        setStateConfiguration(stateConfig);
      }
      
      setLoading(false);
    } catch (e) {
      console.error('Error loading configurations:', e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (feathers) {
      loadStateElectionConfig();
    }
  }, [feathers, matchName]);

  const loadParties = async () => {
    if (!feathers) return;
    try {
      const res = await feathers.getService('parties').find({ query: { $limit: 100 } });
      const parties = Array.isArray(res) ? res : (res?.data || []);
      setPartyOptions(sortPartyOptions(parties.map((p) => ({ key: p.key, name: p.name }))));
    } catch (e) {
      console.error('Error loading parties:', e);
    }
  };

  useEffect(() => {
    if (feathers) {
      loadParties();
    }
  }, [feathers]);

  const onSave = async () => {
    if (saving) return;

    setSaving(true);
    try {
      // Determine if setup is complete (both primary and runoff modes are set)
      const isComplete = !!(stateElectionConfig.primary && stateElectionConfig.runoff);
      
      // Prepare config to save - convert default selectPrompt to null
      const defaultSelectPrompt = 'We only want government sites.';
      const supportedPartiesKeys = (stateElectionConfig.supportedPartiesForPrimary || []).map((p) =>
        typeof p === 'string' ? p : p.key
      );
      const supportedGeneralPartiesKeys = (stateElectionConfig.supportedPartiesForGeneral || []).map((p) =>
        typeof p === 'string' ? p : p.key
      );
      const configToSave = {
        ...stateElectionConfig,
        supportedPartiesForPrimary: supportedPartiesKeys,
        supportedPartiesForGeneral: supportedGeneralPartiesKeys,
        setupComplete: isComplete,
      };
      
      // If selectPrompt is the default, save as null so lambda uses its default
      if (configToSave.autoScouter?.search?.selectPrompt === defaultSelectPrompt) {
        configToSave.autoScouter.search.selectPrompt = null;
      }
      
      if (stateElectionConfig._id) {
        await feathers.getService('state-election-configurations').patch(
          stateElectionConfig._id,
          configToSave
        );
      } else {
        // Create new config
        const created = await feathers.getService('state-election-configurations').create(configToSave);
        setStateElectionConfig(created);
      }
      if (onEditingChange) {
        onEditingChange(false);
      }
      await loadStateElectionConfig();
    } catch (e) {
      console.error('Error saving state election config:', e);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    loadStateElectionConfig();
    if (onEditingChange) {
      onEditingChange(false);
    }
  };

  const handlePrimaryModeChange = (value) => {
    setStateElectionConfig({
      ...stateElectionConfig,
      primary: value || undefined,
    });
  };

  const handlePartiesChange = (event) => {
    const value = event.target.value;
    const keys = typeof value === 'string' ? value.split(',') : value;
    setStateElectionConfig({
      ...stateElectionConfig,
      supportedPartiesForPrimary: keys,
    });
  };

  const handlePartyCreated = (party) => {
    if (party?.key && !partyOptions.some((p) => p.key === party.key)) {
      setPartyOptions(sortPartyOptions([...partyOptions, { key: party.key, name: party.name }]));
    }
    const currentKeys = (stateElectionConfig.supportedPartiesForPrimary || []).map((p) =>
      typeof p === 'string' ? p : p.key
    );
    if (party?.key && !currentKeys.includes(party.key)) {
      setStateElectionConfig({
        ...stateElectionConfig,
        supportedPartiesForPrimary: [...currentKeys, party.key],
      });
    }
  };

  const handleGeneralPartiesChange = (event) => {
    const value = event.target.value;
    const keys = typeof value === 'string' ? value.split(',') : value;
    setStateElectionConfig({
      ...stateElectionConfig,
      supportedPartiesForGeneral: keys,
    });
  };

  const handleGeneralPartyCreated = (party) => {
    if (party?.key && !partyOptions.some((p) => p.key === party.key)) {
      setPartyOptions(sortPartyOptions([...partyOptions, { key: party.key, name: party.name }]));
    }
    const currentKeys = (stateElectionConfig.supportedPartiesForGeneral || []).map((p) =>
      typeof p === 'string' ? p : p.key
    );
    if (party?.key && !currentKeys.includes(party.key)) {
      setStateElectionConfig({
        ...stateElectionConfig,
        supportedPartiesForGeneral: [...currentKeys, party.key],
      });
    }
  };

  const handleNonpartisanPrimaryModeChange = (checked) => {
    setStateElectionConfig({
      ...stateElectionConfig,
      nonpartisanPrimaryMode: checked 
        ? 'NONPARTISAN_PRIMARY_MODE_DECIDED_IN_PRIMARY' 
        : 'NONPARTISAN_PRIMARY_MODE_DECIDED_IN_GENERAL',
    });
  };

  const handleUncontestedPrimariesChange = (value) => {
    setStateElectionConfig({
      ...stateElectionConfig,
      uncontestedPrimariesAppearOnBallot: value,
    });
  };

  const handleRunoffModeChange = (value) => {
    setStateElectionConfig({
      ...stateElectionConfig,
      runoff: value || undefined,
    });
  };

  if (loading) {
    return (
      <div>
        <Skeleton variant="rectangular" height={200} style={{ marginBottom: '24px' }} />
        <Skeleton variant="rectangular" height={400} />
      </div>
    );
  }

  if (!stateElectionConfig) {
    return (
      <Typography variant="body1" color="textSecondary">
        Unable to load state election configuration.
      </Typography>
    );
  }

  const selectedPrimaryMode = PRIMARY_MODE_OPTIONS.find(
    opt => opt.value === stateElectionConfig.primary
  );
  const selectedRunoffMode = RUNOFF_MODE_OPTIONS.find(
    opt => opt.value === stateElectionConfig.runoff
  );
  const supportedPartiesRaw = stateElectionConfig.supportedPartiesForPrimary || [];
  const supportedParties = supportedPartiesRaw.map((p) => (typeof p === 'string' ? p : p.key));
  const supportedGeneralPartiesRaw = stateElectionConfig.supportedPartiesForGeneral || [];
  const supportedGeneralParties = supportedGeneralPartiesRaw.map((p) => (typeof p === 'string' ? p : p.key));
  const nonpartisanDecidedInPrimary = stateElectionConfig.nonpartisanPrimaryMode === 'NONPARTISAN_PRIMARY_MODE_DECIDED_IN_PRIMARY';
  const selectedUncontestedPrimariesOption = UNCONTESTED_PRIMARIES_OPTIONS.find(
    opt => opt.value === stateElectionConfig.uncontestedPrimariesAppearOnBallot
  ) || UNCONTESTED_PRIMARIES_OPTIONS[0];

  return (
    <div>
      {editing && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <Button
            variant="text"
            onClick={cancelEdit}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={saving}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
      
      <Paper style={{ padding: '24px', marginBottom: '24px' }}>
        <Typography variant="h6" style={{ marginBottom: '16px', 'fontWeight': 'bold'}}>
          Primary Elections
        </Typography>
        
        <Box style={{ marginBottom: '24px' }}>
          {editing && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Typography variant="body1" style={{ fontWeight: '500' }}>
              Primary Mode
            </Typography>
            <Tooltip
              title="Select the type of primary election system used in this state. This determines how voters can participate in primary elections."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>}
          {editing ? (
            <FormControl fullWidth>
              <Select
                value={stateElectionConfig.primary || ''}
                onChange={(e) => handlePrimaryModeChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None selected</em>
                </MenuItem>
                {PRIMARY_MODE_OPTIONS.map(option => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    style={{ whiteSpace: 'normal', padding: '8px 16px' }}
                  >
                    <div style={{ width: '100%' }}>
                      <div style={{ marginBottom: '4px' }}><strong>{option.label}</strong></div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>{option.description}</div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <div>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedPrimaryMode ? selectedPrimaryMode.label : 'Not configured'}
              </Typography>
              {selectedPrimaryMode && (
                <Typography 
                  variant="body2" 
                  color="textSecondary" 
                  style={{ marginTop: '4px'}}
                >
                  {selectedPrimaryMode.description}
                </Typography>
              )}
            </div>
          )}
        </Box>

        {(selectedPrimaryMode || editing) && <Box style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Typography variant="body1" style={{ fontWeight: '600' }}>
              Supported Parties for Primary
            </Typography>
            <Tooltip
              title="Select which parties are available for voters to choose from in primary elections."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>
          {editing ? (
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={supportedParties}
                  onChange={handlePartiesChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const partyOption = partyOptions.find((opt) => opt.key === value);
                        return (
                          <Chip key={value} label={partyOption ? partyOption.name : value} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {partyOptions.map((party) => (
                    <MenuItem key={party.key} value={party.key}>
                      {party.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <CreatePartyModal
                onCreate={handlePartyCreated}
                buttonComponent={({ onClick }) => (
                  <Tooltip title="Add party">
                    <span>
                      <IconButton
                        onClick={onClick}
                        size="small"
                        sx={{
                          color: '#666',
                          '&:hover': {
                            color: '#1976d2',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              />
            </Box>
          ) : (
            <div style={{ marginTop: '-8px'}}>
              {supportedParties.length > 0 ? (
                <Typography variant="body1">
                  {supportedPartiesRaw.map((p) => {
                    if (typeof p === 'object' && p?.name) return p.name;
                    const partyOption = partyOptions.find((opt) => opt.key === (typeof p === 'string' ? p : p?.key));
                    return partyOption ? partyOption.name : (typeof p === 'string' ? p : p?.key);
                  }).join(', ')}
                </Typography>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No parties selected
                </Typography>
              )}
            </div>
          )}
        </Box>}

        {(selectedPrimaryMode || editing) && <Box style={{ marginBottom: '12px' }}>
          {editing && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Typography variant="body1" style={{ fontWeight: '600' }}>
              Uncontested primaries on ballot
            </Typography>
            <Tooltip
              title="Choose whether primary races with only one candidate appear on voter ballots."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>}
          {editing ? (
            <FormControl fullWidth>
              <Select
                value={stateElectionConfig.uncontestedPrimariesAppearOnBallot || 'yes'}
                onChange={(e) => handleUncontestedPrimariesChange(e.target.value)}
              >
                {UNCONTESTED_PRIMARIES_OPTIONS.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    style={{ whiteSpace: 'normal', padding: '8px 16px' }}
                  >
                    <div style={{ width: '100%' }}>
                      <div style={{ marginBottom: '4px' }}><strong>{option.label}</strong></div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>{option.description}</div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <div>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedUncontestedPrimariesOption.displayTitle}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                {selectedUncontestedPrimariesOption.displayDescription}
              </Typography>
            </div>
          )}
        </Box>}

        {(selectedPrimaryMode || editing) && <Box>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={nonpartisanDecidedInPrimary}
                  onChange={(e) => {
                    if (editing) {
                      handleNonpartisanPrimaryModeChange(e.target.checked);
                    }
                  }}
                  style={{ pointerEvents: editing ? 'auto' : 'none' }}
                />
              }
              label="Nonpartisan races decided in primary"
            />
            <Tooltip
              title="When checked, nonpartisan races are decided in the primary election and do not move on to a general election."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>
        </Box>}
      </Paper>

      <Paper style={{ padding: '24px', marginBottom: '24px' }}>
        <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 'bold' }}>
          General Elections
        </Typography>

        <Box>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Typography variant="body1" style={{ fontWeight: '600' }}>
              Supported Parties for General
            </Typography>
            <Tooltip
              title="Select which parties are available for general elections."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>
          {editing ? (
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={supportedGeneralParties}
                  onChange={handleGeneralPartiesChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const partyOption = partyOptions.find((opt) => opt.key === value);
                        return (
                          <Chip key={value} label={partyOption ? partyOption.name : value} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {partyOptions.map((party) => (
                    <MenuItem key={party.key} value={party.key}>
                      {party.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <CreatePartyModal
                onCreate={handleGeneralPartyCreated}
                buttonComponent={({ onClick }) => (
                  <Tooltip title="Add party">
                    <span>
                      <IconButton
                        onClick={onClick}
                        size="small"
                        sx={{
                          color: '#666',
                          '&:hover': {
                            color: '#1976d2',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              />
            </Box>
          ) : (
            <div style={{ marginTop: '-8px' }}>
              {supportedGeneralParties.length > 0 ? (
                <Typography variant="body1">
                  {supportedGeneralPartiesRaw.map((party) => {
                    if (typeof party === 'object' && party?.name) return party.name;
                    const key = typeof party === 'string' ? party : party?.key;
                    const partyOption = partyOptions.find((option) => option.key === key);
                    return partyOption ? partyOption.name : key;
                  }).join(', ')}
                </Typography>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No parties selected
                </Typography>
              )}
            </div>
          )}
        </Box>
      </Paper>

      <Paper style={{ padding: '24px' }}>
        <Typography variant="h6" style={{ marginBottom: '16px', 'fontWeight': 'bold' }}>
          Runoff Elections
        </Typography>
        
        <Box>
          {editing && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Typography variant="body1" style={{ fontWeight: '500' }}>
              Runoff Mode
            </Typography>
            <Tooltip
              title="Select the type of runoff election system used in this state. This determines when a runoff election is required."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>}
          {editing ? (
            <FormControl fullWidth>
              <Select
                value={stateElectionConfig.runoff || ''}
                onChange={(e) => handleRunoffModeChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None selected</em>
                </MenuItem>
                {RUNOFF_MODE_OPTIONS.map(option => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    style={{ whiteSpace: 'normal', padding: '8px 16px' }}
                  >
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                      <div style={{ marginBottom: '4px' }}><strong>{option.label}</strong></div>
                      <div style={{ fontSize: '0.8em', color: '#666', wordWrap: 'break-word' }}>{option.description}</div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <div>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedRunoffMode ? selectedRunoffMode.label : 'Not configured'}
              </Typography>
              {selectedRunoffMode && (
                <Typography 
                  variant="body2" 
                  color="textSecondary" 
                  style={{ marginTop: '4px'}}
                >
                  {selectedRunoffMode.description}
                </Typography>
              )}
            </div>
          )}
        </Box>
      </Paper>

      <Paper style={{ padding: '24px', marginTop: '24px' }}>
        <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 'bold' }}>
          Scouting
        </Typography>
        
        <Box style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Typography variant="body1" style={{ fontWeight: '600' }}>
              EAU Coverage
            </Typography>
            <Tooltip
              title="Election Administration Units (EAUs) are the units of our scouting operations. The default coverage fields below determine which scouting districts to mark as complete, depending on the type of scouting."
              placement="top"
            >
              <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
            </Tooltip>
          </div>
          
          {stateConfiguration && (() => {
            // Get EAU types and 'state' type, with 'state' first
            const eauTypes = stateConfiguration.typeConfigurations
              .filter(tc => tc.isEauType || tc.type === 'state')
              .map(tc => tc.type)
              .sort((a, b) => {
                // Put 'state' first
                if (a === 'state') return -1;
                if (b === 'state') return 1;
                return 0;
              });
            
            const allDistrictTypes = stateConfiguration.typeConfigurations.map(tc => tc.type);
            
            return eauTypes.map(eauType => {
              const scoutingConfigForType = stateElectionConfig.scoutingConfiguration?.[eauType] || {};
              const fullCoverage = scoutingConfigForType.fullCoverage || [];
              const partialCoverage = scoutingConfigForType.partialCoverage || [];
              const noCoverage = scoutingConfigForType.noCoverage || [];
              
              // State is configurable in addition to tier-2 district types.
              const allConfiguredTypes = [...new Set([...fullCoverage, ...partialCoverage, ...noCoverage])];
              const configuredTypes = allConfiguredTypes.filter(type => {
                const typeConfig = stateConfiguration.typeConfigurations.find(tc => tc.type === type);
                return typeConfig && (type === 'state' || typeConfig.tierNumber === 2);
              });
              
              // Get all available types (exclude the EAU type itself)
              const allAvailableTypes = allDistrictTypes.filter(type => type !== eauType);
              
              // Helper to get coverage extent for a district type
              const getCoverageExtent = (districtType) => {
                if (fullCoverage.includes(districtType)) return 'fullCoverage';
                if (partialCoverage.includes(districtType)) return 'partialCoverage';
                if (noCoverage.includes(districtType)) return 'noCoverage';
                return null;
              };
              
              // Helper to update coverage for a district type
              const handleDistrictTypeCoverageChange = (districtType, coverageExtent) => {
                const newFullCoverage = fullCoverage.filter(t => t !== districtType);
                const newPartialCoverage = partialCoverage.filter(t => t !== districtType);
                const newNoCoverage = noCoverage.filter(t => t !== districtType);
                
                if (coverageExtent === 'fullCoverage') {
                  newFullCoverage.push(districtType);
                } else if (coverageExtent === 'partialCoverage') {
                  newPartialCoverage.push(districtType);
                } else if (coverageExtent === 'noCoverage') {
                  newNoCoverage.push(districtType);
                }
                
                const newScoutingConfig = {
                  ...(stateElectionConfig.scoutingConfiguration || {}),
                  [eauType]: {
                    fullCoverage: newFullCoverage,
                    partialCoverage: newPartialCoverage,
                    noCoverage: newNoCoverage,
                  }
                };
                
                setStateElectionConfig({
                  ...stateElectionConfig,
                  scoutingConfiguration: newScoutingConfig,
                });
              };
              
              // Handle adding a new district type
              const handleAddDistrictType = (districtType) => {
                // Add to partialCoverage by default
                const newPartialCoverage = [...partialCoverage, districtType];
                
                const newScoutingConfig = {
                  ...(stateElectionConfig.scoutingConfiguration || {}),
                  [eauType]: {
                    fullCoverage: fullCoverage,
                    partialCoverage: newPartialCoverage,
                    noCoverage: noCoverage,
                  }
                };
                
                setStateElectionConfig({
                  ...stateElectionConfig,
                  scoutingConfiguration: newScoutingConfig,
                });
              };
              
              return (
                <Box key={eauType} style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <Typography variant="body1" style={{ fontWeight: '500' }}>
                      {humanizeDistrictType(eauType, stateConfiguration.typeConfigurations)}
                    </Typography>
                    {editing && (
                      <AddDistrictTypeModal
                        stateName={stateElectionConfig.stateName}
                        existingTypes={configuredTypes}
                        excludeTypes={eauType === 'state' ? [] : [eauType]}
                        restrictToTiers={[1, 2]}
                        onSelect={handleAddDistrictType}
                        buttonComponent={({ onClick }) => (
                          <Tooltip title="Add district type">
                            <span>
                              <IconButton
                                onClick={onClick}
                                size="small"
                                sx={{
                                  color: '#666',
                                  '&:hover': {
                                    color: '#1976d2',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                  }
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      />
                    )}
                  </div>
                  
                  {editing ? (
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {configuredTypes.length > 0 ? (
                        configuredTypes.map(districtType => {
                          const currentCoverage = getCoverageExtent(districtType);
                          
                          return (
                            <Box key={districtType} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <Typography variant="body1" style={{ minWidth: '120px', fontWeight: '500' }}>
                                {humanizeDistrictType(districtType, stateConfiguration.typeConfigurations)}
                              </Typography>
                              <FormControl size="small" style={{ minWidth: 200 }}>
                                <Select
                                  value={currentCoverage || ''}
                                  onChange={(e) => handleDistrictTypeCoverageChange(districtType, e.target.value || null)}
                                  displayEmpty
                                  sx={{
                                    backgroundColor: 'white',
                                    '& .MuiSelect-select': {
                                      padding: '8px 32px 8px 12px'
                                    }
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Not set</em>
                                  </MenuItem>
                                  <MenuItem value="fullCoverage">Mark all as complete</MenuItem>
                                  <MenuItem value="partialCoverage">Mark only those in the content as complete</MenuItem>
                                  <MenuItem value="noCoverage">Mark none as complete</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="textSecondary" style={{}}>
                          No district types configured. Click the + button to add one.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" style={{ lineHeight: '1.6' }}>
                      {generateScoutingDescription(eauType, scoutingConfigForType, stateConfiguration.typeConfigurations)}
                    </Typography>
                  )}
                </Box>
              );
            });
          })()}
          
          {!stateConfiguration && (
            <Typography variant="body2" color="textSecondary">
              Loading state configuration...
            </Typography>
          )}
        </Box>

          <Box style={{ marginTop: '24px' }}>
            <Typography variant="body1" style={{ fontWeight: '600', marginBottom: '0', marginTop: '16px' }}>
              Auto Scouting
            </Typography>
            <Typography variant="body1" color="textPrimary" style={{ marginBottom: '16px' }}>
              Below are the default configurations for the auto scouter, in the order they are used.
            </Typography>

            <Box style={{ marginTop: '36px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', width: 'fit-content', alignItems: 'center', backgroundColor: '#fff', marginTop: '-34px', padding: '4px 8px', alignSelf: 'flex-start' }}>
                <Typography variant="overline" color="textPrimary" style={{ width: 'fit-content' }}>
                  Search
                </Typography>
                <Tooltip
                  title="This determines how the main elections page is found for the EAU"
                  placement="top"
                >
                  <InfoOutlined style={{ fontSize: '14px', color: '#666', cursor: 'help' }} />
                </Tooltip>
              </div>
              {editing ? (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
                  <FormControl fullWidth size="small" style={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
                    <Typography variant="body1" color="textPrimary" style={{fontWeight: '600' }}>
                      Mode
                    </Typography>
                    <Select
                      value={stateElectionConfig.autoScouter?.search?.mode || 'google'}
                      onChange={(e) => {
                        setStateElectionConfig({
                          ...stateElectionConfig,
                          autoScouter: {
                            ...stateElectionConfig.autoScouter,
                            search: {
                              ...stateElectionConfig.autoScouter?.search,
                              mode: e.target.value,
                            }
                          }
                        });
                      }}
                    >
                      <MenuItem value="google">Google</MenuItem>
                    </Select>
                  </FormControl>

                  {stateElectionConfig.autoScouter?.search?.mode === 'google' && (
                    <>
                      <FormControl fullWidth style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <Typography variant="body1" color="textPrimary" style={{ fontWeight: '600' }}>
                            Query
                          </Typography>
                          <Tooltip
                            title="The search query template used to find the elections page. Use {stateName} and {districtName} as merge tags that will be replaced with actual values."
                            placement="top"
                          >
                            <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
                          </Tooltip>
                        </div>
                        <PromptEditor
                          style={{ marginTop: '-36px' }}
                          value={stateElectionConfig.autoScouter?.search?.query || ''}
                          onChange={(nextValue) => {
                            setStateElectionConfig({
                              ...stateElectionConfig,
                              autoScouter: {
                                ...stateElectionConfig.autoScouter,
                                search: {
                                  ...stateElectionConfig.autoScouter?.search,
                                  query: nextValue,
                                }
                              }
                            });
                          }}
                          placeholder="e.g., {stateName} - {districtName} - elections clerk"
                          multiline={false}
                        />
                      </FormControl>

                      <FormControl fullWidth>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <Typography variant="body1" color="textPrimary" style={{ fontWeight: '600' }}>
                            Select prompt
                          </Typography>
                          <Tooltip
                            title="Custom instructions for selecting the best search result. If not provided, the system default is used. You can insert merge tags like {stateName}, {districtName}, and {electionName}."
                            placement="top"
                          >
                            <InfoOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
                          </Tooltip>
                        </div>
                        <Typography variant="body1" color="textPrimary" style={{ marginBottom: '8px' }}>
                         We're trying to find the sample ballot and candidate list information for <PlaceholderVariable>districtName</PlaceholderVariable>, <PlaceholderVariable>stateName</PlaceholderVariable>, specifically for the upcoming <PlaceholderVariable>electionName</PlaceholderVariable> election. Which of these search results is most likely to have this information from an official government source?
                        </Typography>
                        <PromptEditor
                          value={stateElectionConfig.autoScouter?.search?.selectPrompt || ''}
                          onChange={(nextValue) => {
                            setStateElectionConfig({
                              ...stateElectionConfig,
                              autoScouter: {
                                ...stateElectionConfig.autoScouter,
                                search: {
                                  ...stateElectionConfig.autoScouter?.search,
                                  selectPrompt: nextValue || null,
                                }
                              }
                            });
                          }}
                          placeholder="Optional additional instructions. Use {stateName}, {districtName}, and {electionName} as merge tags."
                          rows={3}
                        />
                      </FormControl>
                    </>
                  )}
                </Box>
              ) : (
                <Box style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <Typography variant="body1" color="textPrimary">
                    <strong>Mode:</strong> {stateElectionConfig.autoScouter?.search?.mode || 'google'}
                  </Typography>
                  {stateElectionConfig.autoScouter?.search?.mode === 'google' && (
                    <>
                      <Typography variant="body1" color="textPrimary" style={{ marginTop: '4px' }}>
                        <strong>Query:</strong> {stateElectionConfig.autoScouter?.search?.query || 'Not set'}
                      </Typography>
                      <Typography variant="body1" color="textPrimary" style={{ marginTop: '4px' }}>
                        <strong>Select Prompt:</strong> {stateElectionConfig.autoScouter?.search?.selectPrompt || 'We only want government sites.'}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box style={{ marginTop: '48px', marginBottom: '24px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', width: 'fit-content', alignItems: 'center', backgroundColor: '#fff', marginTop: '-34px', padding: '4px 8px', alignSelf: 'flex-start' }}>
              <Typography variant="overline" color="textPrimary" style={{ width: 'fit-content' }}>
                Crawling
              </Typography>
              <Tooltip
                title="These are the main instructions used when the computer-use model crawls the elections site. Use merge tags like {startingUrl}, {stateName}, {districtName}, and {electionName}."
                placement="top"
              >
                <InfoOutlined style={{ fontSize: '14px', color: '#666', cursor: 'help' }} />
              </Tooltip>
            </div>
            {editing ? (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography variant="body2" color="textSecondary">
                  Use merge tags to keep this template reusable across states and districts.
                </Typography>
                <div>
                  <Typography variant="body1" color="textPrimary" style={{ fontWeight: '600' }}>Crawl prompt</Typography>
                  <PromptEditor
                    style={{ marginTop: '-24px' }}
                    value={stateElectionConfig.autoScouter?.crawl?.crawlPrompt || ''}
                    onChange={(nextValue) => {
                      setStateElectionConfig({
                        ...stateElectionConfig,
                        autoScouter: {
                          ...stateElectionConfig.autoScouter,
                          crawl: {
                            ...(stateElectionConfig.autoScouter?.crawl || {}),
                            crawlPrompt: nextValue || null,
                          },
                        },
                      });
                    }}
                    placeholder="Write high-level instructions for how to explore the elections site. You can reference {startingUrl}, {stateName}, {districtName}, and {electionName}."
                    rows={12}
                  />
                </div>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'pre-wrap' }}>
                {stateElectionConfig.autoScouter?.crawl?.crawlPrompt || 'Using system default crawl instructions.'}
              </Typography>
            )}
          </Box>
          
          <Box style={{ marginTop: '48px', marginBottom: '24px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', width: 'fit-content', alignItems: 'center', backgroundColor: '#fff', marginTop: '-34px', padding: '4px 8px', alignSelf: 'flex-start' }}>
              <Typography variant="overline" color="textPrimary" style={{ width: 'fit-content' }}>
                Parsing
              </Typography>
              <Tooltip
                title="These instructions are appended to the system defaults when the model parses sample ballots, candidate lists, and measure lists. Use merge tags like {stateName}, {districtName}, and {electionName}."
                placement="top"
              >
                <InfoOutlined style={{ fontSize: '14px', color: '#666', cursor: 'help' }} />
              </Tooltip>
            </div>
            {editing ? (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Typography variant="body1" color="textPrimary" style={{ fontWeight: '600' }}>Sample Ballot parsing</Typography>
                  <PromptEditor
                    style={{ marginTop: '-24px' }}
                    value={stateElectionConfig.autoScouter?.parse?.sampleBallot || ''}
                    onChange={(nextValue) => {
                      setStateElectionConfig({
                        ...stateElectionConfig,
                        autoScouter: {
                          ...stateElectionConfig.autoScouter,
                          parse: {
                            ...(stateElectionConfig.autoScouter?.parse || {}),
                            sampleBallot: nextValue || null,
                          },
                        },
                      });
                    }}
                    placeholder="Instructions for parsing individual sample ballots. You can reference {stateName}, {districtName}, and {electionName}."
                    rows={8}
                  />
                </div>
                <div style={{ marginTop: '24px' }}>
                  <Typography variant="body1" color="textPrimary" style={{ fontWeight: '600' }}>Candidate List parsing</Typography>
                  <PromptEditor
                    style={{ marginTop: '-24px' }}
                    value={stateElectionConfig.autoScouter?.parse?.candidateList || ''}
                    onChange={(nextValue) => {
                      setStateElectionConfig({
                        ...stateElectionConfig,
                        autoScouter: {
                          ...stateElectionConfig.autoScouter,
                          parse: {
                            ...(stateElectionConfig.autoScouter?.parse || {}),
                            candidateList: nextValue || null,
                          },
                        },
                      });
                    }}
                    placeholder="Instructions for parsing candidate lists. You can reference {stateName}, {districtName}, and {electionName}."
                    rows={8}
                  />
                </div>
                <div style={{ marginTop: '24px' }}>
                  <Typography variant="body1" color="textPrimary" style={{ fontWeight: '600' }}>Measure List parsing</Typography>
                  <PromptEditor
                    style={{ marginTop: '-24px' }}
                    value={stateElectionConfig.autoScouter?.parse?.measureList || ''}
                    onChange={(nextValue) => {
                      setStateElectionConfig({
                        ...stateElectionConfig,
                        autoScouter: {
                          ...stateElectionConfig.autoScouter,
                          parse: {
                            ...(stateElectionConfig.autoScouter?.parse || {}),
                            measureList: nextValue || null,
                          },
                        },
                      });
                    }}
                    placeholder="Instructions for parsing measure lists. You can reference {stateName}, {districtName}, and {electionName}."
                    rows={8}
                  />
                </div>
              </Box>
            ) : (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'pre-wrap' }}>
                  {stateElectionConfig.autoScouter?.parse?.sampleBallot || 'Using system default sample ballot parse instructions.'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'pre-wrap' }}>
                  {stateElectionConfig.autoScouter?.parse?.candidateList || 'Using system default candidate list parse instructions.'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'pre-wrap' }}>
                  {stateElectionConfig.autoScouter?.parse?.measureList || 'Using system default measure list parse instructions.'}
                </Typography>
              </Box>
            )}
          </Box>
      </Paper>
    </div>
  );
};

const PlaceholderVariable = styled.span`
  font-family: monospace;
  font-size: 13px;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 2px 6px;
`;

export default StateElectionConfiguration;
